import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/utils';
import { Panel } from '@/shared/ui';
import { WebFilterPlacementKind } from '@/api/generated/schemas';
import type { PlacementOrderEditor } from '../../internal/usePlacementOrder';
import {
  visibleGroupsForEnabledKinds,
  type PlacementOrderGroup,
  type PlacementOrderRow,
} from '../../internal/placementOrderModel';
import type { WebFilterFormApi } from '../useWebFilterForm';
import { selectEnabledKindsFromForm } from '../../internal/usePlacementOrder';

export interface PlacementOrderPanelProps {
  form: WebFilterFormApi;
  editor: PlacementOrderEditor;
}

function kindLabelKey(kind: WebFilterPlacementKind): string {
  switch (kind) {
    case WebFilterPlacementKind.MenuBanner:
      return 'webFilter.display.modes.banner';
    case WebFilterPlacementKind.MenuTile:
      return 'webFilter.display.modes.tile';
    case WebFilterPlacementKind.MenuSpecial:
      return 'webFilter.display.modes.special';
    case WebFilterPlacementKind.MenuLink:
      return 'webFilter.display.modes.link';
    case WebFilterPlacementKind.SearchSpecialPeriods:
      return 'webFilter.display.search.special';
    case WebFilterPlacementKind.SearchTourTypes:
      return 'webFilter.display.search.tourTypes';
    case WebFilterPlacementKind.HomeFindTour:
      return 'webFilter.display.hp.cardTitle';
    default:
      return kind;
  }
}

/** Panel řazení filtrů v sekcích webového projektu. */
export function PlacementOrderPanel({ form, editor }: PlacementOrderPanelProps) {
  const { t } = useTranslation('www');
  const { state, reorder, isLoading } = editor;
  const [dragState, setDragState] = useState<{
    kind: WebFilterPlacementKind;
    index: number;
  } | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  return (
    <form.Subscribe selector={(formState) => formState.values}>
      {(values) => {
        const enabledKinds = selectEnabledKindsFromForm(values);
        if (enabledKinds.length === 0) {
          return (
            <Panel variant="filled" icon="sort" title={t('webFilter.order.title')}>
              <p className="text-phx-text-muted text-phx-sm p-phx-2">
                {t('webFilter.order.emptyHint')}
              </p>
            </Panel>
          );
        }

        const visibleGroups = visibleGroupsForEnabledKinds(state, enabledKinds);

        return (
          <Panel
            variant="filled"
            icon="sort"
            title={t('webFilter.order.title')}
            subtitle={t('webFilter.order.subtitle')}
          >
            {isLoading && visibleGroups.length === 0 ? (
              <p className="text-phx-text-muted text-phx-sm p-phx-2">
                {t('webFilter.order.loading')}
              </p>
            ) : null}

            <div className="gap-phx-4 flex flex-col">
              {visibleGroups.map((group) => (
                <PlacementOrderGroupSection
                  key={group.kind}
                  group={group}
                  kindLabel={t(kindLabelKey(group.kind))}
                  dragKind={dragState?.kind === group.kind ? dragState.index : null}
                  dragOverIndex={dragState?.kind === group.kind ? dragOverIndex : null}
                  onDragStart={(index) => setDragState({ kind: group.kind, index })}
                  onDragEnd={() => {
                    setDragState(null);
                    setDragOverIndex(null);
                  }}
                  onDragOver={(index) => setDragOverIndex(index)}
                  onDrop={(index) => {
                    if (dragState && dragState.kind === group.kind) {
                      reorder(group.kind, dragState.index, index);
                    }
                    setDragState(null);
                    setDragOverIndex(null);
                  }}
                />
              ))}
            </div>
          </Panel>
        );
      }}
    </form.Subscribe>
  );
}

interface PlacementOrderGroupSectionProps {
  group: PlacementOrderGroup;
  kindLabel: string;
  dragKind: number | null;
  dragOverIndex: number | null;
  onDragStart: (index: number) => void;
  onDragEnd: () => void;
  onDragOver: (index: number) => void;
  onDrop: (index: number) => void;
}

function PlacementOrderGroupSection({
  group,
  kindLabel,
  dragKind,
  dragOverIndex,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: PlacementOrderGroupSectionProps) {
  return (
    <section data-testid={`placement-order-group-${group.kind}`}>
      <h3 className="text-phx-text-muted text-phx-xs mb-phx-2 font-extrabold tracking-wide uppercase">
        {kindLabel}
      </h3>
      <div className="flex flex-col">
        {group.items.map((item, index) => (
          <Fragment key={rowKey(item, index)}>
            <DropIndicator
              visible={dragKind !== null && dragOverIndex === index && dragKind !== index}
            />
            <PlacementOrderRow
              item={item}
              dragging={dragKind === index}
              onDragStart={() => onDragStart(index)}
              onDragEnd={onDragEnd}
              onDragOver={(event) => {
                if (dragKind !== null) {
                  event.preventDefault();
                  onDragOver(index);
                }
              }}
              onDrop={() => onDrop(index)}
            />
          </Fragment>
        ))}
        {dragKind !== null ? (
          <div
            onDragOver={(event) => {
              event.preventDefault();
              onDragOver(group.items.length);
            }}
            onDrop={() => onDrop(group.items.length)}
            className="py-phx-1"
          >
            <DropIndicator visible={dragOverIndex === group.items.length} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

function rowKey(item: PlacementOrderRow, index: number): string {
  return item.id ?? `current-${item.isCurrentFilter}-${index}`;
}

interface PlacementOrderRowProps {
  item: PlacementOrderRow;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: (event: React.DragEvent) => void;
  onDrop: () => void;
}

function PlacementOrderRow({
  item,
  dragging,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: PlacementOrderRowProps) {
  return (
    <div
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={cn(
        'border-phx-border bg-phx-surface-raised gap-phx-2 px-phx-3 py-phx-2 flex items-center rounded-md border',
        dragging && 'opacity-50',
        item.isCurrentFilter && 'border-phx-primary/40 bg-phx-primary/5'
      )}
    >
      <span
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        className="text-phx-text-faint cursor-grab select-none active:cursor-grabbing"
        aria-hidden
      >
        ⠿
      </span>
      <span className="text-phx-text-muted text-phx-xs w-5 shrink-0 tabular-nums">
        {item.order}
      </span>
      <span className={cn('text-phx-sm flex-1 truncate', item.isCurrentFilter && 'font-semibold')}>
        {item.filterName}
      </span>
    </div>
  );
}

function DropIndicator({ visible }: { visible: boolean }) {
  if (!visible) {
    return null;
  }
  return (
    <div
      aria-hidden
      className="bg-phx-primary my-px h-0.5 rounded-full"
      data-testid="drop-indicator"
    />
  );
}
